'use client';

import Link from 'next/link';

import { zodResolver } from '@hookform/resolvers/zod';
import { CheckIcon, ExclamationTriangleIcon } from '@radix-ui/react-icons';
import { ArrowRightIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import type { z } from 'zod';

import { useUpdateUser } from '@kit/supabase/hooks/use-update-user-mutation';
import { Alert, AlertDescription, AlertTitle } from '@kit/ui/alert';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@kit/ui/form';
import { Trans } from '@kit/ui/trans';

import InputAuth from '../components/input';
import { PasswordResetSchema } from '../schemas/password-reset.schema';
import { useTranslation } from 'react-i18next';
import { AuthButton } from './auth-button';

export function UpdatePasswordForm(params: { redirectTo: string }) {
  const updateUser = useUpdateUser();
  const { t } = useTranslation('auth');

  const form = useForm<z.infer<typeof PasswordResetSchema>>({
    resolver: zodResolver(PasswordResetSchema),
    defaultValues: {
      password: '',
      repeatPassword: '',
    },
  });

  if (updateUser.error) {
    return <ErrorState onRetry={() => updateUser.reset()} />;
  }

  if (updateUser.data && !updateUser.isPending) {
    return <SuccessState redirectTo={params.redirectTo} />;
  }

  return (
    <Form {...form}>
      <form
        className={'w-full space-y-4'}
        onSubmit={form.handleSubmit(({ password }) => {
          return updateUser.mutateAsync({
            password,
            redirectTo: params.redirectTo,
          });
        })}
      >
        <FormField
          control={form.control}
          name={'password'}
          render={({ field, fieldState }) => (
            <FormItem>
              <FormControl>
                <InputAuth
                  required
                  type="password"
                  placeholder={t('common:password')}
                  error={!!fieldState.error}
                  {...field}
                />
              </FormControl>

              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name={'repeatPassword'}
          render={({ field, fieldState }) => (
            <FormItem>
              <FormControl>
                <InputAuth
                  required
                  type="password"
                  placeholder={t('common:confirmPassword')}
                  error={!!fieldState.error}
                  {...field}
                />
              </FormControl>

              <FormMessage />
            </FormItem>
          )}
        />

        <AuthButton
          loading={updateUser.isPending}
          disabled={updateUser.isPending}
          data-test="auth-submit-button"
          loadingText="auth:resettingPassword"
        >
          <Trans i18nKey={'auth:passwordResetLabel'} />
        </AuthButton>
      </form>
    </Form>
  );
}

function SuccessState(props: { redirectTo: string }) {
  return (
    <div className={'flex flex-col space-y-4'}>
      <Alert variant={'success'}>
        <CheckIcon className={'w-4'} />

        <AlertTitle>
          <Trans i18nKey={'account:updatePasswordSuccess'} />
        </AlertTitle>

        <AlertDescription>
          <Trans i18nKey={'account:updatePasswordSuccessMessage'} />
        </AlertDescription>
      </Alert>

      <Link href={props.redirectTo}>
        <AuthButton>
          <span>
            <Trans i18nKey={'common:backToHomePage'} />
          </span>

          <ArrowRightIcon className={'h-4'} />
        </AuthButton>
      </Link>
    </div>
  );
}

function ErrorState(props: { onRetry: () => void }) {
  return (
    <div className={'flex flex-col space-y-4'}>
      <Alert variant={'destructive'} className='border-[#E48787]/50 text-[#E48787] dark:border-[#E48787] [&>svg]:text-[#E48787]'>
        <ExclamationTriangleIcon className={'w-4'} />

        <AlertTitle>
          <Trans i18nKey={'common:genericError'} />
        </AlertTitle>

        <AlertDescription>
          <Trans i18nKey={'auth:resetPasswordError'} />
        </AlertDescription>
      </Alert>

      <AuthButton onClick={props.onRetry}>
        <Trans i18nKey={'common:retry'} />
      </AuthButton>
    </div>
  );
}
